import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Info, Search } from 'lucide-react';
import {
  ERROR_CODES,
  SALES_INQUIRY_STATUSES,
  type PanelSalesInquiryRowDto,
  type SalesInquiryStatus,
} from '@icebreaker/shared';
import {
  Avatar,
  OrgMark,
  Button,
  EmptyState,
  Input,
  Skeleton,
  Table,
  Tag,
  useErrorMessage,
} from '@icebreaker/ui';
import { salesInquiryDetailPath } from '@/app/routes';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import adminStyles from '@/features/admin/ui/admin.module.css';
import { SALES_PAGE_SIZE, toSalesQuery } from '../model/sales-query';
import {
  calendarDayInstant,
  SALES_STATUS_VARIANT,
  shortDateOptions,
  inquiryKindLabel,
} from '../model/sales-view';
import {
  fetchSalesInquiries,
  salesFilterChanged,
  salesFiltersReset,
  type SalesStatusFilter,
} from '../store/sales-inquiries.slice';
import { SalesInquiryDetail } from './sales-inquiry-detail';
import styles from './sales.module.css';

const DETAIL_PARAM = 'aday';

/**
 * SATIŞ ADAYLARI LİSTESİ (P1).
 *
 * "Tümü" varsayılandır (referans: kaybolmaması için "Yeni" varsayılan değil),
 * "Yeni" sayısı sekmede yazılı. Satıra tıklayınca detay SAĞDAN açılır (P2) ve
 * adres `?aday=<kimlik>` taşır: liste arkada soluk kalır, personel yerini
 * kaybetmeden adaydan adaya geçer.
 */
export function SalesInquiriesScreen() {
  const t = useTranslation();
  const errorMessage = useErrorMessage();
  const dispatch = useAppDispatch();
  const state = useAppSelector((root: RootState) => root.panelSales);
  const { filters, list } = state;
  const [params, setParams] = useSearchParams();
  const openId = params.get(DETAIL_PARAM);
  const [searchDraft, setSearchDraft] = useState(filters.q);

  const refresh = useCallback(() => {
    dispatch(fetchSalesInquiries.request(toSalesQuery(filters)));
  }, [dispatch, filters]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Arama yazarken her harfte istek atılmaz; kısa bir duraklamada süzülür.
  useEffect(() => {
    if (searchDraft === filters.q) return undefined;
    const handle = window.setTimeout(() => dispatch(salesFilterChanged({ q: searchDraft })), 300);
    return () => window.clearTimeout(handle);
  }, [dispatch, filters.q, searchDraft]);

  useEffect(() => {
    const onOnline = () => refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  const closeDetail = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete(DETAIL_PARAM);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const narrowed = filters.status !== 'all' || filters.q.trim() !== '';
  const firstLoad = list.phase === 'loading' && list.items.length === 0;
  const counts = list.statusCounts;
  const allCount = counts
    ? SALES_INQUIRY_STATUSES.reduce((sum, key) => sum + counts[key], 0)
    : null;
  const from = list.total === 0 ? 0 : (list.page - 1) * SALES_PAGE_SIZE + 1;
  const to = Math.min(list.page * SALES_PAGE_SIZE, list.total);

  const tabs: readonly { value: SalesStatusFilter; label: string; count: number | null }[] = [
    { value: 'all', label: t.t('admin.sales.all'), count: allCount },
    ...SALES_INQUIRY_STATUSES.map((status) => ({
      value: status,
      label: t.t(`admin.sales.statuses.${status}`),
      count: counts ? counts[status] : null,
    })),
  ];

  return (
    <section className={adminStyles.screen}>
      <div className={adminStyles.titleRow}>
        <h1 className={adminStyles.title}>{t.t('admin.sales.title')}</h1>
        {allCount !== null ? (
          <span className={adminStyles.titleMeta}>
            {t.t('admin.sales.total', { count: allCount })}
          </span>
        ) : null}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.tabs} role="group" aria-label={t.t('admin.sales.tabsLabel')}>
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              aria-pressed={filters.status === tab.value}
              className={[styles.tab, filters.status === tab.value ? styles.tabActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => dispatch(salesFilterChanged({ status: tab.value }))}
            >
              {tab.label}
              {tab.count !== null ? (
                <span className={styles.tabCount}>{t.formatNumber(tab.count)}</span>
              ) : null}
            </button>
          ))}
        </div>
        <label className={styles.search}>
          <Search size={16} aria-hidden="true" className={styles.searchIcon} />
          <Input
            className={styles.searchInput}
            type="search"
            aria-label={t.t('admin.sales.searchLabel')}
            placeholder={t.t('admin.sales.searchPlaceholder')}
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
      </div>
      <p className={styles.auditNote}>
        <Info size={15} aria-hidden="true" className={styles.auditIcon} />
        {t.t('admin.sales.auditNote')}
      </p>

      {list.phase === 'failed' && list.items.length === 0 ? (
        <div className={styles.errorCard} role="alert">
          <p className={styles.errorTitle}>{t.t('admin.sales.errorTitle')}</p>
          <p className={styles.errorBody}>
            {/* İz yazılamadıysa liste bilerek gelmedi (C21): sebep söylenir. */}
            {list.error?.code === ERROR_CODES.AUDIT_UNAVAILABLE
              ? errorMessage(list.error)
              : t.t('admin.sales.errorBody')}
          </p>
          <Button variant="secondary" size="sm" onClick={refresh}>
            {t.t('admin.sales.retry')}
          </Button>
        </div>
      ) : list.phase === 'succeeded' && list.items.length === 0 ? (
        <div className={styles.stateBox}>
          {narrowed ? (
            <EmptyState
              title={t.t('admin.sales.filterEmptyTitle')}
              body={filterEmptyBody(t, filters.status, filters.q.trim())}
              action={
                <Button
                  variant="primary"
                  onClick={() => {
                    setSearchDraft('');
                    dispatch(salesFiltersReset());
                  }}
                >
                  {t.t('admin.sales.clearFilter')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              title={t.t('admin.sales.emptyTitle')}
              body={t.t('admin.sales.emptyBody')}
              preview={<TablePreview />}
            />
          )}
        </div>
      ) : (
        <div className={[styles.tableArea, 'ib-priority-table'].join(' ')}>
          <Table aria-label={t.t('admin.sales.title')}>
            <thead>
              <tr>
                <th>{t.t('admin.sales.table.date')}</th>
                <th>{t.t('admin.sales.table.company')}</th>
                <th>{t.t('admin.sales.table.type')}</th>
                <th className={styles.numberHead}>{t.t('admin.sales.table.attendees')}</th>
                <th className={[styles.optionalCol, 'ib-col-optional'].join(' ')}>
                  {t.t('admin.sales.table.estimatedDate')}
                </th>
                <th>{t.t('admin.sales.table.contact')}</th>
                <th>
                  <span className={styles.statusHead}>
                    {t.t('admin.sales.table.status')}
                    <span className={[styles.hiddenNote, 'ib-col-note'].join(' ')}>
                      {t.t('admin.sales.hiddenColumns', { count: 2 })}
                    </span>
                  </span>
                </th>
                <th className={[styles.optionalCol, 'ib-col-optional'].join(' ')}>
                  {t.t('admin.sales.table.organization')}
                </th>
              </tr>
            </thead>
            <tbody>
              {firstLoad
                ? Array.from({ length: 4 }, (_, index) => (
                    <tr key={`skeleton-${index}`}>
                      <td colSpan={8}>
                        {index === 0 ? (
                          <Skeleton label={t.t('admin.sales.loading')} lines={1} lineHeight={14} />
                        ) : (
                          <Skeleton decorative lines={1} lineHeight={14} />
                        )}
                      </td>
                    </tr>
                  ))
                : list.items.map((row) => (
                    <LeadRow key={row.id} row={row} selected={row.id === openId} />
                  ))}
            </tbody>
          </Table>
        </div>
      )}

      {list.total > 0 ? (
        <div className={styles.pager}>
          <span className={styles.pagerRange}>
            {t.t('admin.sales.range', {
              from: t.formatNumber(from),
              to: t.formatNumber(to),
              total: t.formatNumber(list.total),
            })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={filters.page <= 1}
            onClick={() => dispatch(salesFilterChanged({ page: Math.max(1, filters.page - 1) }))}
          >
            {t.t('admin.overview.prevPage')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!list.hasNext}
            onClick={() => dispatch(salesFilterChanged({ page: filters.page + 1 }))}
          >
            {t.t('admin.overview.nextPage')}
          </Button>
        </div>
      ) : null}

      {openId ? <SalesInquiryDetail id={openId} onClose={closeDetail} /> : null}
    </section>
  );
}

function LeadRow({ row, selected }: { row: PanelSalesInquiryRowDto; selected: boolean }) {
  const t = useTranslation();
  const contactName = row.contactName;
  return (
    <tr className={[styles.clickableRow, selected ? styles.rowSelected : ''].join(' ')}>
      <td className={styles.dateCell}>
        {t.formatDate(row.createdAt, shortDateOptions(row.createdAt))}
      </td>
      <td className={styles.companyCell}>
        <Link className={styles.rowLink} to={salesInquiryDetailPath(row.id)}>
          {row.companyName ?? t.t('admin.sales.anonymized')}
        </Link>
      </td>
      <td>
        <Tag variant="interest">{inquiryKindLabel(t, row)}</Tag>
      </td>
      <td className={styles.numberCell}>
        {row.estimatedAttendees !== null
          ? t.formatNumber(row.estimatedAttendees)
          : (row.attendeeBand ?? '—')}
      </td>
      <td className={[styles.monthCell, styles.optionalCol, 'ib-col-optional'].join(' ')}>
        {row.estimatedDate
          ? t.formatDate(calendarDayInstant(row.estimatedDate), { month: 'short', year: 'numeric' })
          : '—'}
      </td>
      <td>
        <span className={styles.personCell}>
          {contactName ? (
            <Avatar name={contactName} size="xs" />
          ) : (
            <span className={styles.neutralMark} aria-hidden="true">
              —
            </span>
          )}
          <span className={[styles.personName, contactName ? '' : styles.muted].join(' ')}>
            {contactName ?? t.t('admin.sales.anonymized')}
          </span>
        </span>
      </td>
      <td>
        <Tag variant={SALES_STATUS_VARIANT[row.status]}>
          {t.t(`admin.sales.statuses.${row.status}`)}
        </Tag>
      </td>
      <td className={[styles.optionalCol, 'ib-col-optional'].join(' ')}>
        <span className={styles.orgCell}>
          {row.organization ? (
            <>
              <OrgMark
                name={row.organization.name}
                logoUrl={null}
                fill={row.organization.brandFill}
                size="xs"
              />
              <span className={styles.personName}>{row.organization.name}</span>
            </>
          ) : (
            '—'
          )}
        </span>
      </td>
    </tr>
  );
}

function filterEmptyBody(
  t: ReturnType<typeof useTranslation>,
  status: SalesStatusFilter,
  q: string,
): string {
  const statusName =
    status === 'all' ? null : t.t(`admin.sales.statuses.${status satisfies SalesInquiryStatus}`);
  if (statusName && q) return t.t('admin.sales.filterEmptyBoth', { status: statusName, q });
  if (statusName) return t.t('admin.sales.filterEmptyStatus', { status: statusName });
  return t.t('admin.sales.filterEmptyQuery', { q });
}

/** Boş durumun soluk, kesik çizgili tablo önizlemesi (README 4.7). */
function TablePreview() {
  return (
    <>
      {['70%', '52%', '62%'].map((width) => (
        <div key={width} className={styles.previewRow}>
          <span className={styles.previewBar} />
          <span className={styles.previewBar} style={{ width }} />
          <span className={styles.previewBar} />
          <span className={styles.previewBar} style={{ width }} />
          <span className={styles.previewBar} />
        </div>
      ))}
    </>
  );
}
